/**
 * Вставка ссылки в текст с разметкой [текст](https://адрес).
 * Если в поле выделен текст — он становится текстом ссылки на своём месте; иначе ссылка вставляется у курсора.
 * Кнопка «Ссылка» ищет поле ввода рядом с собой (внутри того же <label>).
 */
export function insertLinkAtSelection(event: Event, current: string, apply: (value: string) => void) {
  const field = (event.currentTarget as HTMLElement | null)?.closest('label')?.querySelector('textarea, input') as HTMLTextAreaElement | HTMLInputElement | null;
  const start = field?.selectionStart ?? current.length;
  const end = field?.selectionEnd ?? current.length;
  const selected = current.slice(start, end).trim();
  const url = window.prompt(selected ? `Адрес ссылки для «${selected.slice(0, 60)}» (https://…)` : 'Адрес ссылки (https://…)', 'https://');
  if (!url || !/^(https?:\/\/|mailto:)\S+$/i.test(url.trim())) return;
  const label = selected || window.prompt('Текст ссылки', 'Подробнее');
  if (!label) return;
  const before = current.slice(0, start);
  const after = current.slice(end);
  const pad = (text: string, side: 'before' | 'after') => (selected || !text ? '' : side === 'before' ? (/\s$/.test(text) ? '' : ' ') : /^\s|^[.,;:!?)»]/.test(text) ? '' : ' ');
  const link = `[${label.replace(/[[\]\n]/g, ' ').trim()}](${url.trim()})`;
  const value = `${before}${pad(before, 'before')}${link}${pad(after, 'after')}${after}`;
  apply(value);
  // Курсор — сразу после ссылки.
  const caret = before.length + pad(before, 'before').length + link.length;
  requestAnimationFrame(() => { field?.focus(); field?.setSelectionRange(caret, caret); });
}

/** Подсказка, если ссылка оформлена с ошибкой: после «]» сразу должна идти «(адрес)». */
export function brokenLinkHint(text: string) {
  if (!text) return '';
  if (/\[[^\]\n]+\](?!\()/.test(text)) return 'Ссылка оформлена с ошибкой: сразу после «]» без пробелов и другого текста должно идти «(https://адрес)». Проще выделить слова и нажать «Ссылка».';
  if (/\]\s+\(https?:/i.test(text)) return 'Между «]» и «(адрес)» не должно быть пробела.';
  return '';
}

/** Ссылки, размеченные в тексте как [текст](адрес), с их позициями. */
const linkPattern = /\[([^\]\n]+)\]\(((?:https?:\/\/|mailto:)[^\s)]+)\)/gi;
export type TextLink = { label: string; url: string; start: number; end: number };
export function textLinks(text: string): TextLink[] {
  return [...(text ?? '').matchAll(linkPattern)].map((m) => ({ label: m[1], url: m[2], start: m.index ?? 0, end: (m.index ?? 0) + m[0].length }));
}
const cleanLabel = (label: string) => label.replace(/[[\]\n]/g, ' ').trim() || 'Подробнее';
/** Меняет текст или адрес ссылки номер `index`. */
export function updateTextLink(text: string, index: number, label: string, url: string) {
  const link = textLinks(text)[index];
  if (!link) return text;
  return `${text.slice(0, link.start)}[${cleanLabel(label)}](${url.trim().replace(/[\s)]/g, '')})${text.slice(link.end)}`;
}
/** Убирает ссылку номер `index`, оставляя её текст обычными словами. */
export function unlinkTextLink(text: string, index: number) {
  const link = textLinks(text)[index];
  return link ? `${text.slice(0, link.start)}${link.label}${text.slice(link.end)}` : text;
}
/** Добавляет ссылку в конец текста. */
export function appendTextLink(text: string, label: string, url: string) {
  const base = (text ?? '').trimEnd();
  return `${base}${base ? ' ' : ''}[${cleanLabel(label)}](${url.trim()})`;
}
/** Отдельная ссылка согласия (старый формат: linkLabel/linkUrl) переносится в текст, чтобы ссылок могло быть несколько. */
export function moveConsentLinkToText<T extends { text: string; linkLabel?: string; linkUrl?: string }>(consent: T): T {
  if (!consent.linkUrl) return { ...consent, linkLabel: '', linkUrl: '' };
  return { ...consent, text: appendTextLink(consent.text, consent.linkLabel || 'Подробнее', consent.linkUrl), linkLabel: '', linkUrl: '' };
}
